import { useSearchParams } from '@remix-run/react'
import { useState } from 'react'
import { Icon } from '#app/shared/ui/icons/Icon.js'
import { Checkbox } from '#app/shared/ui/inputs/Checkbox.js'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import { CategoryEnum, CarColorEnum } from '../../../../../types/enums'

interface StaticFilters {
  carrocerias: string[]
  colores: string[]
  puertas: number[]
  plazas: number[]
}

type FilterCarroceriaProps = {
  categoryCounts: Record<string, number>
  colorCounts: Record<string, number>
  doorsCounts: Record<string, number>
  seatsCounts: Record<string, number>
}

type AccordionSection = 'carroceria' | 'color' | 'puertas' | 'plazas'

const staticFilters: StaticFilters = {
  carrocerias: Object.values(CategoryEnum),
  colores: Object.values(CarColorEnum),
  puertas: [2, 3, 4, 5],
  plazas: [2, 3, 4, 5, 6, 7, 8, 9],
}

export const FilterCarroceria = ({
  categoryCounts,
  colorCounts,
  doorsCounts,
  seatsCounts,
}: FilterCarroceriaProps): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [expandedSection, setExpandedSection] =
    useState<AccordionSection | null>(null)

  const handleCheckboxChange = (
    filterKey: string,
    value: string | number,
    checked: boolean,
  ) => {
    const normalizeValue =
      filterKey === 'puertas' && typeof value === 'number' && value > 5
        ? 5
        : filterKey === 'plazas' && typeof value === 'number' && value > 9
          ? 9
          : value

    const currentFilters =
      searchParams.get(filterKey)?.split(',').filter(Boolean) || []
    const updatedFilters = checked
      ? [...currentFilters, normalizeValue]
      : currentFilters.filter((f: string) => f !== String(normalizeValue))

    const newSearchParams = new URLSearchParams(searchParams.toString())
    if (updatedFilters.length > 0) {
      newSearchParams.set(filterKey, updatedFilters.join(','))
    } else {
      newSearchParams.delete(filterKey)
    }
    setSearchParams(newSearchParams, { replace: true })
  }

  const toggleSection = (section: AccordionSection) => {
    setExpandedSection(expandedSection === section ? null : section)
  }

  const AccordionHeader = ({
    title,
    isExpanded,
    onClick,
  }: {
    title: string
    isExpanded: boolean
    onClick: () => void
  }) => (
    <button
      onClick={onClick}
      className={`
        flex w-full items-center justify-between border-t border-neutral-100
        py-4
      `}
    >
      <span className="text-base-medium">{title}</span>
      <Icon
        name="basic/chevron-down"
        className={`
          h-5 w-5 transform text-neutral-400 transition-transform duration-300
          ease-in-out

          ${isExpanded ? `rotate-180` : ''}
        `}
      />
    </button>
  )

  return (
    <MainContainer>
      <div
        className={`
          flex w-full flex-col

          lg:w-[67.4%] lg:flex-row lg:justify-between
        `}
      >
        <div
          className={`
            w-full

            lg:w-auto
          `}
        >
          <div className="lg:hidden">
            <AccordionHeader
              title="Carrocería"
              isExpanded={expandedSection === 'carroceria'}
              onClick={() => toggleSection('carroceria')}
            />
          </div>
          <div
            className={`
              px-4

              ${expandedSection === 'carroceria' ? 'block' : 'hidden'}

              lg:block lg:px-0
            `}
          >
            <h3
              className={`
                mb-5 hidden text-base-bold

                lg:block
              `}
            >
              Carrocería
            </h3>
            <div
              className={`
                flex flex-col gap-3 pb-4

                lg:pb-0
              `}
            >
              {staticFilters.carrocerias.map((carroceria) => {
                const count = categoryCounts[carroceria] ?? 0
                return (
                  <div
                    key={carroceria}
                    className={`
                      flex items-center gap-2 text-base-medium text-neutral-900
                    `}
                  >
                    <Checkbox
                      id={carroceria}
                      checked={
                        searchParams
                          .get('carroceria')
                          ?.split(',')
                          .includes(carroceria) || false
                      }
                      onCheckedChange={(checked) =>
                        handleCheckboxChange(
                          'carroceria',
                          carroceria,
                          !!checked,
                        )
                      }
                      disabled={count === 0}
                    />
                    <label
                      htmlFor={carroceria}
                      className={count === 0 ? 'text-gray-400' : ''}
                    >
                      {carroceria} ({count})
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div
          className={`
            w-full

            lg:w-auto
          `}
        >
          <div className="lg:hidden">
            <AccordionHeader
              title="Color"
              isExpanded={expandedSection === 'color'}
              onClick={() => toggleSection('color')}
            />
          </div>
          <div
            className={`
              px-4

              ${expandedSection === 'color' ? 'block' : 'hidden'}

              lg:block lg:px-0
            `}
          >
            <h3
              className={`
                mb-5 hidden text-base-bold

                lg:block
              `}
            >
              Color
            </h3>
            <div
              className={`
                flex flex-col gap-3 pb-4

                lg:pb-0
              `}
            >
              {staticFilters.colores.map((color) => {
                const count = colorCounts[color] ?? 0
                return (
                  <div
                    key={color}
                    className={`
                      flex items-center gap-2 text-base-medium text-neutral-700
                    `}
                  >
                    <Checkbox
                      id={color}
                      checked={
                        searchParams.get('color')?.split(',').includes(color) ||
                        false
                      }
                      onCheckedChange={(checked) =>
                        handleCheckboxChange('color', color, !!checked)
                      }
                      disabled={count === 0}
                    />
                    <label
                      htmlFor={color}
                      className={count === 0 ? 'text-gray-400' : ''}
                    >
                      {color} ({count})
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div
          className={`
            w-full

            lg:w-auto
          `}
        >
          <div className="lg:hidden">
            <AccordionHeader
              title="Puertas"
              isExpanded={expandedSection === 'puertas'}
              onClick={() => toggleSection('puertas')}
            />
          </div>
          <div
            className={`
              px-4

              ${expandedSection === 'puertas' ? 'block' : 'hidden'}

              lg:block lg:px-0
            `}
          >
            <h3
              className={`
                mb-5 hidden text-base-bold

                lg:block
              `}
            >
              Puertas
            </h3>
            <div
              className={`
                flex flex-col gap-3 pb-4

                lg:pb-0
              `}
            >
              {staticFilters.puertas.map((puerta) => {
                const count = doorsCounts[String(puerta)] ?? 0
                return (
                  <div
                    key={puerta}
                    className={`
                      flex items-center gap-2 text-base-medium text-neutral-700
                    `}
                  >
                    <Checkbox
                      id={String(puerta)}
                      checked={
                        searchParams
                          .get('puertas')
                          ?.split(',')
                          .includes(String(puerta)) || false
                      }
                      onCheckedChange={(checked) =>
                        handleCheckboxChange('puertas', puerta, !!checked)
                      }
                      disabled={count === 0}
                    />
                    <label
                      htmlFor={`${puerta}-puertas`}
                      className={count === 0 ? 'text-gray-400' : ''}
                    >
                      {puerta} puertas ({count})
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div
          className={`
            w-full

            lg:w-auto
          `}
        >
          <div className="lg:hidden">
            <AccordionHeader
              title="Plazas"
              isExpanded={expandedSection === 'plazas'}
              onClick={() => toggleSection('plazas')}
            />
          </div>
          <div
            className={`
              px-4

              ${expandedSection === 'plazas' ? 'block' : 'hidden'}

              lg:block lg:px-0
            `}
          >
            <h3
              className={`
                mb-5 hidden text-base-bold

                lg:block
              `}
            >
              Plazas
            </h3>
            <div
              className={`
                flex flex-col gap-3 pb-4

                lg:pb-0
              `}
            >
              {staticFilters.plazas.map((plaza) => {
                const count = seatsCounts[String(plaza)] ?? 0
                return (
                  <div
                    key={plaza}
                    className={`
                      flex items-center gap-2 text-base-medium text-neutral-700
                    `}
                  >
                    <Checkbox
                      id={String(plaza)}
                      checked={
                        searchParams
                          .get('plazas')
                          ?.split(',')
                          .includes(String(plaza)) || false
                      }
                      onCheckedChange={(checked) =>
                        handleCheckboxChange('plazas', plaza, !!checked)
                      }
                      disabled={count === 0}
                    />
                    <label
                      htmlFor={`${plaza}-plazas`}
                      className={count === 0 ? 'text-gray-400' : ''}
                    >
                      {plaza} plazas ({count})
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </MainContainer>
  )
}
